import language from '../assets/languages/en.json';

export enum ValidationResponse {
  valid = 'valid',
  empty = 'empty',
  shortFullSSN = 'shortFullSSN',
  shortLastFourSSN = 'shortLastFourSSN',
  invalidAge = 'invalidAge',
  invalidDate = 'invalidDate',
  invalidBirthDayDate = 'invalidBirthDayDate',
  invalidBirthDayMonth = 'invalidBirthDayMonth',
  invalidBirthDayYear = 'invalidBirthDayYear',
  invalidFNShort = 'invalidFNShort',
  invalidLNShort = 'invalidLNShort',
  invalidNameLong = 'invalidNameLong',
  invalidName = 'invalidName',
  invalidEmail = 'invalidEmail',
  needEmail = 'needEmail',
  invalidHhEmail = 'invalidHhEmail',
  invalidEmailLong = 'invalidEmailLong',
  invalidPhoneNumber = 'invalidPhoneNumber',
  invalidActivationCode = 'invalidActivationCode',
  invalidSSN = 'invalidSSN',
  invalidSSNMatchLastFour = 'invalidSSNMatchLastFour',
  invalidAddressLong = 'invalidAddressLong',
  invalidAddressShort = 'invalidAddressShort',
  invalidAddressPOBox = 'invalidAddressPOBox',
  invalidCityLong = 'invalidCityLong',
  invalidCityShort = 'invalidCityShort',
  invalidZip = 'invalidZip',
  invalidState = 'invalidState',
  invalidServiceLogin = 'invalidServiceLogin',
  invalidAccountNum = 'invalidAccountNum',
  invalidRoutingNum = 'invalidRoutingNum',
  accountAndRoutingNoMatch = 'accountAndRoutingNoMatch',
  unableVerifyAccount = 'unableVerifyAccount',
  emailPotentialTypo = 'emailPotentialTypo',
  invalidPin = 'invalidPin',
  invalidPinNoMatch = 'invalidPinNoMatch',
  invalidIncome = 'invalidIncome',
  invalidHousing = 'invalidHousing',
}

export function getValidationString(
  r: ValidationResponse,
  language: any
): string {
  switch (r) {
    case ValidationResponse.valid:
      return '';
    case ValidationResponse.invalidAge:
      return language.invalidBirthDateTooYoung;
    case ValidationResponse.invalidDate:
      return language.invalidBirthDate;
    case ValidationResponse.invalidBirthDayDate:
      return language.invalidBirthDayDate;
    case ValidationResponse.invalidBirthDayMonth:
      return language.invalidBirthDayMonth;
    case ValidationResponse.invalidBirthDayYear:
      return language.invalidBirthDayYear;
    case ValidationResponse.invalidFNShort:
      return language.invalidFNShort;
    case ValidationResponse.invalidLNShort:
      return language.invalidLNShort;
    case ValidationResponse.invalidName:
      return language.invalidName;
    case ValidationResponse.invalidNameLong:
      return language.invalidNameLong;
    case ValidationResponse.invalidEmail:
      return language.invalidEmail;
    case ValidationResponse.needEmail:
      return language.enterPersonalEmail;
    case ValidationResponse.invalidHhEmail:
      return language.invalidHhEmail;
    case ValidationResponse.invalidEmailLong:
      return language.invalidEmailLong;
    case ValidationResponse.invalidPhoneNumber:
      return language.invalidPhoneNumber;
    case ValidationResponse.invalidSSN:
      return language.invalidSSN;
    case ValidationResponse.shortLastFourSSN:
      return language.invalidSSN;
    case ValidationResponse.invalidSSNMatchLastFour:
      return language.invalidSSNMatchLastFour;
    case ValidationResponse.shortFullSSN:
      return language.fullSSNErrmsg;
    case ValidationResponse.invalidAddressLong:
      return language.invalidAddressLong;
    case ValidationResponse.invalidCityLong:
      return language.invalidCityLong;
    case ValidationResponse.invalidAddressShort:
      return language.invalidAddressShort;
    case ValidationResponse.invalidAddressPOBox:
      return language.invalidAddressPOBox;
    case ValidationResponse.invalidCityShort:
      return language.invalidCityShort;
    case ValidationResponse.invalidZip:
      return language.invalidZip;
    case ValidationResponse.invalidState:
      return language.invalidState;
    case ValidationResponse.invalidServiceLogin:
      return language.invalidServiceLogin;
    case ValidationResponse.invalidAccountNum:
      return language.invalidAccountNum;
    case ValidationResponse.invalidRoutingNum:
      return language.invalidRoutingNum;
    case ValidationResponse.accountAndRoutingNoMatch:
      return language.accountAndRoutingNoMatch;
    case ValidationResponse.unableVerifyAccount:
      return language.unableVerifyAccount;
    case ValidationResponse.emailPotentialTypo:
      return language.emailTypo;
    case ValidationResponse.invalidPin:
      return language.invalidPin;
    case ValidationResponse.invalidIncome:
      return language.invalidIncome;
    case ValidationResponse.invalidHousing:
      return language.invalidHousing;
    default:
      return 'Something went wrong';
  }
}

const nameMinLength = 2;
const nameMaxLength = 26;
const minAge = 18;
const emailMaxLength = 254;
const addressMaxLength = 40;
const addressMinLength = 2;
const cityMaxLength = 30;
const cityMinLength = 2;

export const validateEmail = (e: string): ValidationResponse => {
  // check length
  if (e.length > emailMaxLength) {
    return ValidationResponse.invalidEmailLong;
  }

  // check valid characters
  const re =
    /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.(com|net|cx|co|edu|org|us|es|gov|me|ca|biz|fr|uk|eu|au|io|mil|info|au|nz|tv|de|jp|cn|es|in|it|nl|se|ch|ru|br|mx|ar|kr|tw|hk|sg|dk|no|fi|be|at|be|cz|ie|pl|pt|tr|za|gr|hu|is|ro)$/;
  if (!re.test(e.toLowerCase())) {
    return ValidationResponse.invalidEmail;
  }

  if (hasDomainTypo(e)) {
    return ValidationResponse.emailPotentialTypo;
  }

  return ValidationResponse.valid;
};

// TODO: add to lib

export const noError: ValidationResponse[] = [
  ValidationResponse.valid,
  ValidationResponse.empty,
  ValidationResponse.emailPotentialTypo,
];
export const hideWhileTyping: ValidationResponse[] = [
  ValidationResponse.empty,
  ValidationResponse.shortFullSSN,
  ValidationResponse.shortLastFourSSN,
  ValidationResponse.invalidAddressShort,
  ValidationResponse.invalidFNShort,
  ValidationResponse.invalidLNShort,
  ValidationResponse.invalidEmail,
  ValidationResponse.invalidPhoneNumber,
  ValidationResponse.invalidCityShort,
  ValidationResponse.invalidZip,
  ValidationResponse.invalidState,
  ValidationResponse.invalidSSN,
  ValidationResponse.shortFullSSN,
  ValidationResponse.shortLastFourSSN,
  ValidationResponse.invalidDate,
  ValidationResponse.invalidBirthDayDate,
  ValidationResponse.invalidBirthDayMonth,
  ValidationResponse.invalidBirthDayYear,
];

const COMMON_PROVIDERS = [
  'gmail.com',
  'yahoo.com',
  'hotmail.com',
  'aol.com',
  'icloud.com',
];

function hasDomainTypo(email: string): boolean {
  const domain = email.split('@').pop()!;
  // If domain typed in is up to 2 characters off
  return COMMON_PROVIDERS.some(
    (provider) =>
      levenshteinDistance(provider, domain) >= 1 &&
      levenshteinDistance(provider, domain) <= 2
  );
}

function levenshteinDistance(s1: string, s2: string): number {
  const dp: number[][] = Array.from({ length: s1.length + 1 }, () =>
    Array(s2.length + 1).fill(0)
  );
  for (let i = 0; i <= s1.length; i++) {
    for (let j = 0; j <= s2.length; j++) {
      if (i === 0) {
        dp[0][j] = j;
      } else if (j === 0) {
        dp[i][0] = i;
      } else {
        dp[i][j] = Math.min(
          dp[i - 1][j - 1] + (s1[i - 1] === s2[j - 1] ? 0 : 1),
          dp[i - 1][j] + 1,
          dp[i][j - 1] + 1
        );
      }
    }
  }
  return dp[s1.length][s2.length];
}

export const validatePhoneNumber = (p: string): ValidationResponse => {
  const result = p.match(/\d/g);
  if (
    result &&
    result[0] !== '0' &&
    result[0] !== '1' &&
    result.join('').length === 10
  ) {
    return ValidationResponse.valid;
  }

  return ValidationResponse.invalidPhoneNumber;
};

export const validateBirthDate = (d: string): ValidationResponse => {
  // check if date is valid format
  const [month, day, year]: string[] = d.split('/');
  const dateObj = new Date(d);
  const currentDate = new Date();

  if (
    !month ||
    month.length !== 2 ||
    Number(month) <= 0 ||
    Number(month) > 12
  ) {
    return ValidationResponse.invalidBirthDayMonth;
  }

  if (!day || day.length !== 2 || Number(day) <= 0 || Number(day) > 31) {
    return ValidationResponse.invalidBirthDayDate;
  }

  if (
    !year ||
    year.length !== 4 ||
    dateObj.getFullYear() < 1900 ||
    dateObj.getFullYear() > currentDate.getFullYear()
  ) {
    return ValidationResponse.invalidBirthDayYear;
  }

  if (
    Number.isNaN(dateObj.getDate()) ||
    // validates that generated date is same as intended date (solves for casting issues)
    dateObj.getDate() !== Number(day) ||
    dateObj.getMonth() !== Number(month) - 1 ||
    dateObj.getFullYear() !== Number(year)
  ) {
    return ValidationResponse.invalidDate;
  }

  // check age
  const diff = currentDate.getTime() - dateObj.getTime();
  const minAgeMS = minAge * 365 * 24 * 60 * 60 * 1000;
  if (diff < minAgeMS) {
    return ValidationResponse.invalidAge;
  }

  return ValidationResponse.valid;
};

export const validateName = (
  n: string,
  isFirst: boolean
): ValidationResponse => {
  // check valid characters
  // eslint-disable-next-line no-useless-escape
  const re = /^[A-Za-zñíáúóüéÑÍÁÚÓÜÉ`'‘’\-\ ,.]*$/;
  if (!re.test(n.toLowerCase())) {
    return ValidationResponse.invalidName;
  }

  if (n.length < nameMinLength) {
    return isFirst
      ? ValidationResponse.invalidFNShort
      : ValidationResponse.invalidLNShort;
  }

  // check against max length
  if (n.length > nameMaxLength) {
    return ValidationResponse.invalidNameLong;
  }

  return ValidationResponse.valid;
};

export const validateAddress = (
  a: string,
  optional?: boolean
): ValidationResponse => {
  if (optional && a.length === 0) {
    return ValidationResponse.valid;
  }

  // po box regex
  const re =
    /(p|p\s|p\.|post)(\s|)(((o\s|o|o\.)|(0\s|0|0\.))|office)(\s|)(box)\b(.*?)/gim;
  if (a.toLowerCase().match(re)) {
    return ValidationResponse.invalidAddressPOBox;
  }

  if (a.length > addressMaxLength) {
    return ValidationResponse.invalidAddressLong;
  }

  if (a.length < addressMinLength) {
    return ValidationResponse.invalidAddressShort;
  }

  return ValidationResponse.valid;
};

export const validateCity = (c: string): ValidationResponse => {
  if (c.length > cityMaxLength) {
    return ValidationResponse.invalidCityLong;
  }

  if (c.length < cityMinLength) {
    return ValidationResponse.invalidCityShort;
  }

  return ValidationResponse.valid;
};

export const validateState = (c: string): ValidationResponse => {
  let validationRes = ValidationResponse.invalidState;
  if (c.length > 0) {
    for (let i = 0; i <= language.stateOption.length; i++) {
      if (c === language.stateOption[i]) {
        validationRes = ValidationResponse.valid;
        break;
      }
    }
  }

  return validationRes;
};

// given [ssn, last4?], returns True if valid SSN
// if last4 is provided, also checks that last4 matches last 4 digits of ssn
// @param ssn - in the form of XXX-XX-XXXX
export const validateFullSSN = (
  ssn: string,
  last4?: string
): ValidationResponse => {
  if (ssn.length < 11) {
    return ValidationResponse.shortFullSSN;
  }

  // can't have 9 as first digit
  // can't have 666, 000 as first 3 digits
  // can't have 00 as middle 2 digits
  // can't have 0000 as last 4 digits
  // correct length/format
  const re = /^(?!666|000)[0-8][0-9_]{2}-(?!00)[0-9_]{2}-(?!0000)[0-9_]{4}$/;
  if (!re.test(ssn)) {
    return ValidationResponse.invalidSSN;
  }

  if (last4 && ssn.slice(-4) !== last4) {
    return ValidationResponse.invalidSSNMatchLastFour;
  }

  return ValidationResponse.valid;
};

export const validateLastFourSSN = (ssn: string): ValidationResponse => {
  if (ssn.length < 4) {
    return ValidationResponse.shortLastFourSSN;
  }

  // cannot be 0000, must be 4 digits
  const re = /^(?!0000)[0-9]{4}$/;
  if (!re.test(ssn)) {
    return ValidationResponse.invalidSSN;
  }

  return ValidationResponse.valid;
};

export const validateZip = (z: string): ValidationResponse => {
  if (z.length < 5) {
    return ValidationResponse.invalidZip;
  }

  return ValidationResponse.valid;
};

const createPinValidators = (pin: string) => {
  let isSame = true;
  let isAscending = true;
  let isDescending = true;

  for (let i = 0; i < pin.length - 1; i++) {
    const curr = pin.charCodeAt(i);
    const next = pin.charCodeAt(i + 1);

    if (pin[i] !== pin[i + 1]) isSame = false;
    if (curr + 1 !== next) isAscending = false;
    if (curr - 1 !== next) isDescending = false;

    if (!isSame && !isAscending && !isDescending) break;
  }

  return {
    pinIsSameDigits: () => isSame,
    pinIsAscendingDigits: () => isAscending,
    pinIsDescendingDigits: () => isDescending,
  };
};

export const isValidPin = (pToValidate: string): ValidationResponse => {
  const p: string[] = Array.from(pToValidate);
  const { pinIsSameDigits, pinIsAscendingDigits, pinIsDescendingDigits } =
    createPinValidators(pToValidate);

  if (
    p.some((val) => isNaN(parseInt(val))) || // Check for invalid characters
    pinIsSameDigits() || // Check for same number
    pinIsAscendingDigits() || // Check for ascending digits
    pinIsDescendingDigits() // Check for descending digits
  ) {
    return ValidationResponse.invalidPin;
  } else {
    return ValidationResponse.valid;
  }
};

export const validateAnnualIncome = (i: string): ValidationResponse => {
  if (i.length === 0 || i === '0' || i === '$' || i === '$0') {
    return ValidationResponse.invalidIncome;
  }

  return ValidationResponse.valid;
};

export const validateHousing = (h: string): ValidationResponse => {
  if (h.length === 0 || h === '$') {
    return ValidationResponse.invalidHousing;
  }

  return ValidationResponse.valid;
};
